/**
 * Backup & Restore admin page
 *
 * Export: select table groups + encryption mode, download as ZIP.
 * Restore: upload ZIP → preflight info card → restore with optional passphrase.
 */
import { useState, useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../services/auth'
import { formatLocalDateTime } from '../../utils/formatDateTime'
import {
  getBackupInfo,
  downloadBackup,
  preflightRestore,
  restoreBackup,
} from '../../services/backup'
import type { BackupInfo, BackupPreflight, EncryptionMode, RestoreResult } from '../../types'

const GROUP_LABELS: Record<string, string> = {
  crm_tables: 'CRM Data',
  settings: 'Settings (SMTP, Notifications)',
  saved_searches: 'Saved Searches',
  users: 'Users & Roles',
  notification_log: 'Notification Log',
}

// Restore section definitions — same groupings as export
const RESTORE_SECTION_KEYS = ['crm_tables', 'settings', 'saved_searches', 'users', 'notification_log'] as const
type RestoreSectionKey = typeof RESTORE_SECTION_KEYS[number]

const RESTORE_SECTION_LABELS: Record<RestoreSectionKey, string> = {
  crm_tables: 'CRM Data',
  settings: 'Settings (SMTP, Notifications)',
  saved_searches: 'Saved Searches',
  users: 'Users & Roles',
  notification_log: 'Notification Log',
}

const ALL_RESTORE_SECTIONS_CHECKED: Record<RestoreSectionKey, boolean> = {
  crm_tables: true,
  settings: true,
  saved_searches: true,
  users: true,
  notification_log: true,
}

const GROUP_DESCRIPTIONS: Record<string, string> = {
  crm_tables: 'Clients, leads, tasks, communications, and notes',
  settings: 'SMTP configuration and notification preferences',
  saved_searches: 'Saved license search filters',
  users: 'User accounts and role assignments — restore will overwrite all users',
  notification_log: 'History of sent notifications',
}

const ENCRYPTION_OPTIONS: { value: EncryptionMode; label: string; description: string }[] = [
  {
    value: 'credentials',
    label: 'Credentials only (recommended)',
    description: 'Protects SMTP configuration and user accounts. CRM data remains readable.',
  },
  {
    value: 'full',
    label: 'Full encryption',
    description: 'Encrypts all backup data. Passphrase required to restore anything.',
  },
  {
    value: 'none',
    label: 'No encryption',
    description: 'SMTP password is stripped. Not recommended if backup may leave your control.',
  },
]

function EyeIcon({ visible }: { visible: boolean }) {
  return visible ? (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
  ) : (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

function EncryptionModeLabel({ mode }: { mode: EncryptionMode }) {
  const labels: Record<EncryptionMode, string> = {
    none: 'None',
    credentials: 'Credentials only',
    full: 'Full encryption',
  }
  return <span>{labels[mode] ?? mode}</span>
}

export default function BackupPage() {
  const { user } = useAuth()

  const [info, setInfo] = useState<BackupInfo | null>(null)
  const [loadError, setLoadError] = useState('')

  // Export state
  const [selections, setSelections] = useState<Record<string, boolean>>({})
  const [encryptionMode, setEncryptionMode] = useState<EncryptionMode>('credentials')
  const [passphrase, setPassphrase] = useState('')
  const [passphraseConfirm, setPassphraseConfirm] = useState('')
  const [showPassphrase, setShowPassphrase] = useState(false)
  const [showPassphraseConfirm, setShowPassphraseConfirm] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')
  const [exportSuccess, setExportSuccess] = useState(false)

  // Restore state
  const [restoreFile, setRestoreFile] = useState<File | null>(null)
  const [preflight, setPreflight] = useState<BackupPreflight | null>(null)
  const [preflighting, setPreflighting] = useState(false)
  const [preflightError, setPreflightError] = useState('')
  const [restorePassphrase, setRestorePassphrase] = useState('')
  const [showRestorePassphrase, setShowRestorePassphrase] = useState(false)
  const [proceedDespiteMismatch, setProceedDespiteMismatch] = useState(false)
  const [restoreMode, setRestoreMode] = useState<'merge' | 'fresh'>('merge')
  const [restoreSections, setRestoreSections] = useState<Record<RestoreSectionKey, boolean>>(
    { ...ALL_RESTORE_SECTIONS_CHECKED }
  )
  const [restoring, setRestoring] = useState(false)
  const [restoreError, setRestoreError] = useState('')
  const [restoreResult, setRestoreResult] = useState<RestoreResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadInfo()
  }, [])

  async function loadInfo() {
    try {
      const data = await getBackupInfo()
      setInfo(data)
      setSelections(data.group_defaults)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load backup info')
    }
  }

  const needsPassphrase = encryptionMode !== 'none'
  const passphraseValid = !needsPassphrase || (passphrase.length > 0 && passphrase === passphraseConfirm)
  const anySelected = Object.values(selections).some(Boolean)
  const exportReady = anySelected && passphraseValid

  async function handleExport() {
    if (!exportReady) return
    setExporting(true)
    setExportError('')
    setExportSuccess(false)
    try {
      await downloadBackup({
        ...selections,
        encryption_mode: encryptionMode,
        passphrase: needsPassphrase ? passphrase : undefined,
      })
      setExportSuccess(true)
      setTimeout(() => setExportSuccess(false), 4000)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  async function handleFileSelect(file: File | null) {
    setRestoreFile(file)
    setPreflight(null)
    setPreflightError('')
    setRestorePassphrase('')
    setRestoreResult(null)
    setRestoreError('')
    setProceedDespiteMismatch(false)
    setRestoreSections({ ...ALL_RESTORE_SECTIONS_CHECKED })

    if (!file) return
    setPreflighting(true)
    try {
      const data = await preflightRestore(file)
      setPreflight(data)
    } catch (err) {
      setPreflightError(err instanceof Error ? err.message : 'Could not read backup file')
    } finally {
      setPreflighting(false)
    }
  }

  const anySectionChecked = RESTORE_SECTION_KEYS.some((k) => restoreSections[k])

  const canRestore =
    restoreFile &&
    preflight &&
    !preflighting &&
    (!preflight.needs_passphrase || restorePassphrase.length > 0) &&
    (!preflight.schema_mismatch || proceedDespiteMismatch) &&
    anySectionChecked

  async function handleRestore() {
    if (!restoreFile || !canRestore) return
    setRestoring(true)
    setRestoreError('')
    setRestoreResult(null)
    try {
      // Build sections string — omit if all are checked (backend default = restore all)
      const checkedSections = RESTORE_SECTION_KEYS.filter((k) => restoreSections[k])
      const allChecked = checkedSections.length === RESTORE_SECTION_KEYS.length
      const sectionsParam = allChecked ? undefined : checkedSections.join(',')

      const result = await restoreBackup(
        restoreFile,
        restoreMode,
        preflight?.needs_passphrase ? restorePassphrase : undefined,
        sectionsParam,
      )
      setRestoreResult(result)
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : 'Restore failed')
    } finally {
      setRestoring(false)
    }
  }

  if (!user?.is_superuser) {
    return <Navigate to="/" replace />
  }

  const groupOrder = info ? Object.keys(info.table_groups) : Object.keys(GROUP_LABELS)

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Backup & Restore</h1>
      <p className="text-sm text-gray-500 mb-8">
        Export CRM and settings data as a ZIP archive. FCC license data is excluded — it can be
        re-downloaded via FCC Import.
      </p>

      {loadError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {loadError}
        </div>
      )}

      {info && (
        <div className="mb-6 p-3 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-500 flex gap-6">
          <span>App: <strong className="text-gray-700">{info.app_version}</strong></span>
          <span>Schema: <strong className="text-gray-700 font-mono">{info.db_schema_version.slice(0, 12)}</strong></span>
        </div>
      )}

      {/* ─── Export ─────────────────────────────────────────────────── */}
      <section className="mb-10">
        <h2 className="text-base font-semibold text-gray-800 mb-4">Export</h2>

        {/* Table group selection */}
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 mb-5">
          {groupOrder.map((group) => (
            <label key={group} className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                checked={selections[group] ?? false}
                onChange={(e) =>
                  setSelections((prev) => ({ ...prev, [group]: e.target.checked }))
                }
              />
              <div>
                <span className="text-sm font-medium text-gray-800">
                  {GROUP_LABELS[group] ?? group}
                </span>
                <p className="text-xs text-gray-500 mt-0.5">
                  {GROUP_DESCRIPTIONS[group] ?? ''}
                </p>
                {info && (
                  <p className="text-xs text-gray-400 mt-0.5 font-mono">
                    {info.table_groups[group]?.join(', ')}
                  </p>
                )}
              </div>
            </label>
          ))}
        </div>

        {/* Encryption mode */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Encryption</label>
          <div className="space-y-2">
            {ENCRYPTION_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="encryptionMode"
                  value={opt.value}
                  checked={encryptionMode === opt.value}
                  onChange={() => {
                    setEncryptionMode(opt.value)
                    setPassphrase('')
                    setPassphraseConfirm('')
                  }}
                  className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-800">{opt.label}</span>
                  <p className="text-xs text-gray-500">{opt.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Passphrase fields (shown when encryption is active) */}
        {needsPassphrase && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Passphrase</label>
              <div className="relative">
                <input
                  type={showPassphrase ? 'text' : 'password'}
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  placeholder="Enter passphrase"
                  className="block w-full rounded-md border border-gray-300 pr-10 py-2 px-3 text-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassphrase((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  <EyeIcon visible={showPassphrase} />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm passphrase</label>
              <div className="relative">
                <input
                  type={showPassphraseConfirm ? 'text' : 'password'}
                  value={passphraseConfirm}
                  onChange={(e) => setPassphraseConfirm(e.target.value)}
                  placeholder="Re-enter passphrase"
                  className="block w-full rounded-md border border-gray-300 pr-10 py-2 px-3 text-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassphraseConfirm((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  <EyeIcon visible={showPassphraseConfirm} />
                </button>
              </div>
              {passphrase && passphraseConfirm && passphrase !== passphraseConfirm && (
                <p className="mt-1 text-xs text-red-600">Passphrases do not match</p>
              )}
            </div>
            <p className="text-xs text-amber-700 font-medium">
              If you lose this passphrase, the backup cannot be decrypted.
            </p>
          </div>
        )}

        {exportError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {exportError}
          </div>
        )}
        {exportSuccess && (
          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
            Backup downloaded successfully.
          </div>
        )}

        <button
          onClick={handleExport}
          disabled={exporting || !exportReady}
          className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? 'Preparing download…' : 'Download Backup'}
        </button>
      </section>

      {/* ─── Restore ────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-base font-semibold text-gray-800 mb-1">Restore</h2>
        <p className="text-sm text-gray-500 mb-4">
          Upload a previously exported backup ZIP. Only tables present in the backup file are
          affected; all other data is untouched.
        </p>

        <div className="space-y-4 mb-4">
          {/* File picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Backup file</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-secondary text-sm"
              >
                Choose file…
              </button>
              <span className="text-sm text-gray-500">
                {restoreFile ? restoreFile.name : 'No file selected'}
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)}
            />
          </div>

          {/* Preflight loading */}
          {preflighting && (
            <p className="text-sm text-gray-500">Reading backup file…</p>
          )}

          {/* Preflight error */}
          {preflightError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {preflightError}
            </div>
          )}

          {/* Preflight info card */}
          {preflight && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <p className="text-sm font-medium text-gray-800">Backup Info</p>
              </div>
              <div className="px-4 py-3 space-y-1 text-sm text-gray-700">
                {preflight.created_at && (
                  <div className="flex gap-2">
                    <span className="text-gray-500 w-24 shrink-0">Created</span>
                    <span>
                      {formatLocalDateTime(preflight.created_at)}
                      {preflight.created_by_email && (
                        <span className="text-gray-500"> by {preflight.created_by_email}</span>
                      )}
                    </span>
                  </div>
                )}
                <div className="flex gap-2">
                  <span className="text-gray-500 w-24 shrink-0">App version</span>
                  <span>{preflight.app_version}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-500 w-24 shrink-0">Schema</span>
                  <span className="font-mono text-xs">{preflight.db_schema_version.slice(0, 16)}</span>
                  {preflight.schema_mismatch ? (
                    <span className="text-amber-700 text-xs font-medium">⚠ differs from current</span>
                  ) : (
                    <span className="text-green-700 text-xs">✓ matches current</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-500 w-24 shrink-0">Encryption</span>
                  <span><EncryptionModeLabel mode={preflight.encryption_mode} /></span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-500 w-24 shrink-0">Tables</span>
                  <span className="text-xs font-mono text-gray-600">{preflight.tables.join(', ')}</span>
                </div>
                {Object.keys(preflight.row_counts).length > 0 && (
                  <div className="flex gap-2">
                    <span className="text-gray-500 w-24 shrink-0">Row counts</span>
                    <span className="text-xs text-gray-600">
                      {Object.entries(preflight.row_counts)
                        .map(([t, n]) => `${t}: ${n}`)
                        .join(' · ')}
                    </span>
                  </div>
                )}
              </div>

              {/* Warnings */}
              {preflight.warnings.length > 0 && (
                <div className="px-4 py-3 border-t border-amber-200 bg-amber-50">
                  <ul className="space-y-1">
                    {preflight.warnings.map((w, i) => (
                      <li key={i} className="text-xs text-amber-800">⚠ {w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Schema mismatch confirmation */}
              {preflight.schema_mismatch && (
                <div className="px-4 py-3 border-t border-amber-200 bg-amber-50">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={proceedDespiteMismatch}
                      onChange={(e) => setProceedDespiteMismatch(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-xs text-amber-800 font-medium">
                      I understand the schema differs and want to proceed anyway
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Restore passphrase (shown when backup needs it) */}
          {preflight?.needs_passphrase && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Passphrase</label>
              <div className="relative">
                <input
                  type={showRestorePassphrase ? 'text' : 'password'}
                  value={restorePassphrase}
                  onChange={(e) => setRestorePassphrase(e.target.value)}
                  placeholder="Enter backup passphrase"
                  className="block w-full rounded-md border border-gray-300 pr-10 py-2 px-3 text-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={() => setShowRestorePassphrase((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  <EyeIcon visible={showRestorePassphrase} />
                </button>
              </div>
            </div>
          )}

          {/* Mode */}
          {preflight && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Restore mode</label>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="restoreMode"
                    value="merge"
                    checked={restoreMode === 'merge'}
                    onChange={() => setRestoreMode('merge')}
                    className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-800">Merge</span>
                    <p className="text-xs text-gray-500">
                      Insert rows that don't already exist — existing data is preserved. Safe for
                      adding backup data to a running system.
                    </p>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="restoreMode"
                    value="fresh"
                    checked={restoreMode === 'fresh'}
                    onChange={() => setRestoreMode('fresh')}
                    className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-800">Fresh</span>
                    <p className="text-xs text-gray-500">
                      Delete all existing data in the backed-up tables, then insert from backup.
                      Use this when setting up a new environment from a backup.{' '}
                      <strong className="text-amber-700">This is destructive.</strong>
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Section filter */}
          {preflight && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Restore sections
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Uncheck sections to skip them. Only tables present in the backup file can be
                restored.
              </p>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {RESTORE_SECTION_KEYS.map((key) => (
                  <label
                    key={key}
                    className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={restoreSections[key]}
                      onChange={(e) =>
                        setRestoreSections((prev) => ({ ...prev, [key]: e.target.checked }))
                      }
                    />
                    <span className="text-sm text-gray-800">{RESTORE_SECTION_LABELS[key]}</span>
                  </label>
                ))}
              </div>
              {RESTORE_SECTION_KEYS.every((k) => !restoreSections[k]) && (
                <p className="mt-1 text-xs text-red-600">
                  At least one section must be selected to restore.
                </p>
              )}
            </div>
          )}
        </div>

        {restoreError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {restoreError}
          </div>
        )}

        {restoreResult && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm font-semibold text-green-800 mb-2">
              Restore complete — {restoreResult.tables_restored} table
              {restoreResult.tables_restored !== 1 ? 's' : ''} restored (mode: {restoreResult.mode})
            </p>
            <div className="table-wrap">
              <table className="text-xs text-gray-700 w-full mb-2">
                <tbody>
                  {Object.entries(restoreResult.row_counts).map(([table, count]) => (
                    <tr key={table}>
                      <td className="font-mono pr-4 py-0.5">{table}</td>
                      <td className="text-right">{count} rows</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {restoreResult.warnings.length > 0 && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                <strong>Warnings:</strong>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  {restoreResult.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleRestore}
          disabled={restoring || !canRestore}
          className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {restoring ? 'Restoring…' : 'Restore Backup'}
        </button>
      </section>
    </div>
  )
}
